/**
 * @fileoverview
 * This file contains various helper functions and constants used across the
 * site. It includes functions for formatting, calculating, and retrieving
 * various data points, as well as constants for low, medium, and high levels
 * used in projections and other metrics.
 */

/**
 * @type {string} The string constant for a "low" level
 */
export const LOW = 'low';

/**
 * @type {string} The CSS class to use for background color when a value is
 * using the LOW constant
 */
export const LOW_BG_CLASS = 'bg-rank-26-40';

/**
 * @type {string} The CSS class to use for text color when a value is
 * using the LOW constant
 */
export const LOW_TEXT_CLASS = 'red-text';

/**
 * @type {string} The string constant for a "medium" level
 */
export const MED = 'med';

/**
 * @type {string} The CSS class to use for background color when a value is
 * using the MED constant
 */
export const MED_BG_CLASS = 'bg-rank-41-59';

/**
 * @type {string} The CSS class to use for text color when a value is
 * using the LOW constant
 */
export const MED_TEXT_CLASS = '';

/**
 * @type {string} The string constant for a "high" level
 */
export const HIGH = 'high';

/**
 * @type {string} The CSS class to use for background color when a value is
 * using the HIGH constant
 */
export const HIGH_BG_CLASS = 'bg-rank-60-75';

/**
 * @type {string} The CSS class to use for text color when a value is
 * using the HIGH constant
 */
export const HIGH_TEXT_CLASS = 'green-text';

/**
 * NOTE: This structure for levels was made after the above constants so we
 * can more easily import this into the WASM code. Honestly it's probably
 * better than defining all of the above constants and we should probably
 * eventually replace all code with using thie LEVELS instead of the above
 * 9 constants.
 *
 * @type {object} Definition for low/medium/high that can be used across various
 * parts of the site that view data this way. Use cases in theory could be
 * for identifying projections that are low/medium/high for a player based on
 * their position, odds of making playoffs, odds of meeting expectations, chance
 * of injury, etc.
 *
 * Each of low/medium/high has the following properties:
 * @property {string} id Identifies something as being low/medium/high
 * @property {string} bgClass The class used for background colors according
 * to the level
 * @property {string} textClass The class used to color text according to
 * an assigned level.
 */
export const LEVELS = {
    low: {
        id: 'low',
        bgClass: LOW_BG_CLASS,
        textClass: LOW_TEXT_CLASS,
    },
    med: {
        id: 'med',
        bgClass: MED_BG_CLASS,
        textClass: MED_TEXT_CLASS,
    },
    high: {
        id: 'high',
        bgClass: HIGH_BG_CLASS,
        textClass: HIGH_TEXT_CLASS,
    },
};

/**
 * @type {array<object>} Each entry is a range DS has deided encapsulates
 * ranks within based on being between 0-100th percentile for a stat. Note
 * that this leaves "gaps". For example, 10.5 exists between group 1 and 2
 * but these numbers are used because they align with CSS class names. This
 * is handled manually in the function that finds the group.
 */
const PERCENTILE_RANK_GROUPS = [
    { min: 0, max: 10 },
    { min: 11, max: 25 },
    { min: 26, max: 40 },
    { min: 41, max: 59 },
    { min: 60, max: 75 },
    { min: 76, max: 89 },
    { min: 90, max: 100 },
];

/**
 * Returns the percentile rank group based on a percent, taking into account
 * the 'gaps'
 * @param {number} percent
 * @returns {string}
 */
const getPercentileRankGroup = percent => {
    // --- Handle percents out of range if needed
    if (percent < 0) {
        return PERCENTILE_RANK_GROUPS[0];
    }
    if (percent >= 100) {
        return PERCENTILE_RANK_GROUPS[PERCENTILE_RANK_GROUPS.length - 1];
    }

    // --- Check if it falls within the group or the "gap"
    for (let i = 0; i < PERCENTILE_RANK_GROUPS.length; i++) {
        const group = PERCENTILE_RANK_GROUPS[i];
        // --- for the max use the max of next element or if @ end use 100
        let max = i + 1 in PERCENTILE_RANK_GROUPS ? PERCENTILE_RANK_GROUPS[i + 1].max : 100;
        if (percent >= group.min && percent < max) {
            return group;
        }
    }
    console.error("Couldn't determine group!!!", percent);
};

/**
 * Returns a class that can be used for a background color of an element
 * based on a percent between 0-100 .
 * @param {number} percent
 * @returns {string}
 */
export const getPercentileRankClass = percent => {
    const group = getPercentileRankGroup(percent);
    return 'bg-rank-' + group.min + '-' + group.max;
};

/**
 * @var string The default print icon used on the site.
 * @see getPrintIcon for use
 */
const PRINT_ICON = `<svg version="1.1" width="20" height="20" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 20 20" xml:space="preserve">
    <path id="print-solid" {fillAttr} d="M17.5,7.5V3c0-0.3-0.1-0.6-0.4-0.9l-1.8-1.8C15.1,0.1,14.8,0,14.5,0H3.8 C3.1,0,2.5,0.6,2.5,1.2v6.2C1.1,7.5,0,8.6,0,10v4.4C0,14.7,0.3,15,0.6,15h1.9v3.8c0,0.7,0.6,1.2,1.2,1.2h12.5c0.7,0,1.2-0.6,1.2-1.2                        V15h1.9c0.3,0,0.6-0.3,0.6-0.6V10C20,8.6,18.9,7.5,17.5,7.5z M15,17.5H5v-3.8h10V17.5z M15,8.8H5V2.5h7.5v1.9c0,0.3,0.3,0.6,0.6,0.6 H15V8.8z M16.9,11.6c-0.5,0-0.9-0.4-0.9-0.9c0-0.5,0.4-0.9,0.9-0.9s0.9,0.4,0.9,0.9c0,0,0,0,0,0C17.8,11.1,17.4,11.6,16.9,11.6z"></path>
</svg>`;

/**
 * Gets the print icon used in the site, applying a fill color if supplied
 * @param {string} fillColor The fill color for the SVG
 * @return string
 */
const getPrintIcon = function (fillColor = '#A41C1F') {
    const fillAttr = fillColor ? 'fill="' + fillColor + '"' : '';
    return PRINT_ICON.replace('{fillAttr}', fillAttr);
};

/**
 * Whether we want to show the bye on the site. As of 2024, the bye should be
 * hidden starting in January and revealed again in May due to the NFL schedule
 * release dates.
 *
 * @return boolean
 */
export function shouldShowBye() {
    return new Date().getMonth() >= 4; // May
}

/**
 * Returns the current season year based on the current date. The season year
 * starts on March 9 and ends on March 8.
 * @return {number}
 * @see [/common/helpers/DsHelper.php#getYear]
 */
export function getSeasonYear() {
    // 0 indexed months, so 2 is March
    const currentMonthNum = new Date().getMonth();
    const currentDayNum = new Date().getDate();
    if (currentMonthNum < 2 || (currentMonthNum === 2 && currentDayNum < 9)) {
        return new Date().getFullYear() - 1;
    }
    return new Date().getFullYear();
}

/**
 * Returns the element of the array of objects where `propName` equals `value`.
 * `propName` defaults to  ID to easily find the item with the given ID from
 * an array of items.
 * @param {any} value The value of the property for the item that we want
 * to have returned
 * @param {array} items Array of items, normally objects, which we want an
 * item returned from
 * @param {string} propName The name of the property to check for a match against
 * value with
 * @param {bool} throwErrorOnNotFound Whether or not to throw the error if an item
 * in the array is not found.
 * @return {object}
 */
export function getArrItemByPropValue(value, items, propName = 'id', throwErrorOnNotFound = true) {
    for (let i = 0; i < items.length; i++) {
        if (items[i][propName] == value) {
            return items[i];
        }
    }
    if (!throwErrorOnNotFound) {
        return null;
    }
    console.error(propName, value, items);
    throw new Error('Could not find item in array with `' + propName + '` equal to "' + value + '"');
}

/**
 * Debug helper function to more easily log Proxy objects in the console.
 */
export const proxyLog = function () {
    const args = Array.from(arguments).map(arg => {
        if (typeof arg === 'object') {
            return JSON.parse(JSON.stringify(arg));
        }
        return arg;
    });
    console.log(...args);
};

export function floatToFixed(value, decimalPlaces = 2) {
    return parseFloat(parseFloat(value).toFixed(decimalPlaces));
}

/**
 * Normalizes a value from an arbitrary range to a 0-1 range.
 * @param {number} value The value to be normalized.
 * @param {number} min The minimum value of the original range.
 * @param {number} max The maximum value of the original range.
 * @returns {number} The normalized value in the rang [0, 1].
 */
export const getPercentInRange = function (value, min, max) {
    // Ensure the value is within the range
    value = Math.max(min, Math.min(max, value));
    // Calculate the normalized value
    return (value - min) / (max - min);
};

/**
 * Returns a CSS string in RGB format for a color based on a percent value.
 * The colors range from red for 0% to orange to yellow for 50% to green for
 * 100%.
 * @param {number} value The percent to calculate the color for. Should be between
 * 0 and 1.
 * @param {boolean} oneIsGreen Whether a value of one should be green or a value
 * of zero should be green
 * @return {string}
 */
export const getColorForPercent = function (value, oneIsGreen = true) {
    // ---  we want to work along a gradient with three points
    // --- for red, yellow, and green
    const red = { r: 222, g: 0, b: 0 };
    const green = { r: 0, g: 128, b: 0 };

    const midPoint = { r: 222, g: 222, b: 0 };
    const lowPoint = oneIsGreen ? red : green;
    const highPoint = oneIsGreen ? green : red;
    let minRed, maxRed, minGreen, maxGreen, percentFull;
    if (value <= 0.5) {
        // --- If we're in th bottom half we're going between the low point
        // --- with red and the mid point with yellow and we get the min and
        // --- max reg and green values from rgb based on those points
        minRed = Math.min(lowPoint.r, midPoint.r);
        maxRed = Math.max(lowPoint.r, midPoint.r);
        minGreen = Math.min(lowPoint.g, midPoint.g);
        maxGreen = Math.max(lowPoint.g, midPoint.g);

        // --- The percent full value we want to use is based on .5 because
        // --- the 'Max' for this range of the gradient is .5
        percentFull = value / 0.5;
    } else {
        // --- If we're in the top half we're going between the mid point
        // --- with yellow and the high point with green
        minRed = Math.min(midPoint.r, highPoint.r);
        maxRed = Math.max(midPoint.r, highPoint.r);
        minGreen = Math.min(midPoint.g, highPoint.g);
        maxGreen = Math.max(midPoint.g, highPoint.g);

        // --- Here we want to reverse the percent full from what it is above
        // --- because we want to start out with FULL red and slowly lessen
        // --- it until it is only green
        percentFull = (1 - value) / value;
    }

    let greenValue = (maxGreen - minGreen) * percentFull + minGreen;
    let redValue = (maxRed - minRed) * percentFull + minRed;

    return 'rgb(' + redValue + ', ' + greenValue + ', 0)';
};

/**
 * Helper function to get nested property
 * @param {object} obj the object to traverse
 * @param {string} path the path to the nested property
 * @return {any} the value of the nested property
 */
export const getNestedProperty = (obj, path) => {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};

/**
 * Sets a nested property on an object using dot notation.
 * @param {object} obj The object to set a property value on
 * @param {string} path The property / path to set, using dot notation like `parentProp.childProp`
 * @param {mixed} value The value to be set
 * @param {boolean} errorIfNotExists Whether to error if the property doesn't
 * already exist
 * @returns {void}
 */
export const setNestedProperty = (obj, path, value, errorIfNotExists = true) => {
    const keys = path.split('.');
    let current = obj;

    // --- Potential error message if needed
    const err = 'Could not set property `' + path + '` on object that did not exist.';

    // --- traverse all keys except the last one
    for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        // Create the nested object if it doesn't exist
        if (!(key in current) || typeof current[key] !== 'object') {
            if (errorIfNotExists) {
                console.error(err, 'object: ', obj, 'value: ', value);
                throw new Error(err);
            }
            current[key] = {};
        }
        current = current[key];
    }

    // --- set the final value
    current[keys[keys.length - 1]] = value;
};
/**
 * Debounce function to limit the rate at which a function can fire
 * @param {function} func The function to debounce
 * @param {number} wait The time to wait before allowing the function to fire again
 * @param {boolean} immediate Whether the function should fire immediately
 * @return {function} The debounced function
 */
export const debounce = (func, wait, immediate) => {
    let timeout;
    return function () {
        let context = this;
        let args = arguments;
        let later = function () {
            timeout = null;
            if (!immediate) func.apply(context, args);
        };
        let callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) func.apply(context, args);
    };
};

/**
 * Returns true if the value is numeric
 * @param {any} val
 * @returns {boolean}
 */
export const isNumeric = function (val) {
    return !isNaN(parseFloat(val)) && isFinite(val);
};

/**
 * Makes a request to an endpoint that counts as a 'gated view'. Helps 'burn'
 * a view on pages that don't refresh
 */
export const burnGatedView = function () {
    fetch('/gated/index');
};

/**
 * @type {boolean|null} Whether or not the user is using the mobile app vs
 * computer web browser. If there is no `window` it's probably executing
 * in a web worker and we return null.
 */
export const isUsingApp =
    typeof window == 'undefined' ? null : window.DRAFTSHARKS_IS_APP || window.navigator.userAgent.includes('DraftSharks');

/**
 * @type {boolean|null} Whether or not the user is using an iOS or Android device
 * If there is no `window` it's probably executing in a web worker and we return null.
 */
export const isUsingiOs = typeof window == 'undefined' ? null : /iPad|iPhone|iPod/.test(window.navigator.userAgent);

/**
 * @type {string} The URL to external storage for the site
 */
export const STORAGE_SERVER_URL = 'https://ds-site.s3.amazonaws.com';

/**
 * This simple depe merge function helps merge objects with nested values but
 * doesn't take into account merging functions ir arrays
 * @param {mixed} target
 * @param {mixed} source
 * @returns
 */
export const simpleDeepMerge = (target, source) => {
    const output = structuredClone(target);

    if (isObject(target) && isObject(source)) {
        Object.keys(source).forEach(key => {
            if (isObject(source[key]) && key in target) {
                output[key] = simpleDeepMerge(target[key], source[key]);
            } else {
                // Handle array concatenation by default if not an object
                if (Array.isArray(source[key]) && Array.isArray(target[key])) {
                    output[key] = target[key].concat(source[key]);
                } else {
                    // Overwrite with source value for primitives or non-mergeable objects
                    output[key] = structuredClone(source[key]);
                }
            }
        });
    }
    return output;
};

function isObject(item) {
    return item && typeof item === 'object' && !Array.isArray(item);
}

const DsHelper = {
    /**
     * Projected values, strength of schdeule values, and other data on the site
     * can be evaluated as 'low', 'medium', or 'high', based on various metrics.
     * Class constants for low, medium and high therefore reside on this generic
     * helper class so we can use them across different areas of the site as
     * identifiers for when stats fall under each of these based on the conditions
     * applicable in the context
     * @var string
     */
    LOW: LOW,
    /**
     * @var string
     */
    MED: MED,
    /**
     * @var string
     */
    HIGH: HIGH,

    /**
     * @var int Total number of regular season games played in a season
     */
    NUM_REGULAR_SEASON_GAMES: 17,

    /**
     * Get the url to the image for the 3d model based on the `percent_low` and
     * `percent_high` values for a Projection or WeeklyPrediction model
     * @var array|common\models\Projection|common\models\WeeklyPrediction
     * @param int $fantasyPosition The `fantasy_position` of the player that the
     * `percent_low` value is for
     * @param array $lowRangePoints Array with fantasy position as keys and value
     * being an array that has two values for the low,high markers
     * @param array $highRangePoints Array with fantasy position as keys and value
     * being an array that has two values for the low,high markers
     * @return string
     */
    get3dImageUrl(projection, fantasyPosition, lowRangePoints, highRangePoints) {
        if (
            !projection ||
            !lowRangePoints[fantasyPosition] ||
            !highRangePoints[fantasyPosition] ||
            (!projection.percent_low && projection.percent_low !== 0) ||
            (!projection.percent_high && projection.percent_high !== 0)
        ) {
            return '/img/3d/unavailable.png';
        }
        let lowRange = getPercentLowLevel(projection.percent_low, fantasyPosition, lowRangePoints);
        let highRange = getPercentHighLevel(projection.percent_high, fantasyPosition, highRangePoints);
        return '/img/3d/C' + highRange + '-F' + lowRange + '.png';
    },

    /**
     * Opens the current page's URL with a print=1 query param for printing
     */
    openPrintVersion: function () {
        let url = new URL(window.location);
        url.searchParams.set('print', 1);
        window.open(url, '_blank');
    },
    getPrintIcon,
    /**
     * Formats a number to use commas and optionally also rounds to the set
     * number of decimal places
     * @param float number
     * @param integer numDecimalPlaces
     */
    formatWithCommas(number, numDecimalPlaces) {
        if (typeof numDecimalPlaces !== 'undefined') {
            let result = number.toFixed(numDecimalPlaces);
            return result.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        }
        return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
    /**
     * Gets age in years (to one decimal place)
     * @param string birthDate
     * @return string
     */
    calculateAgeFromBirthData(birthDate) {
        // --- JS uses milliseconds so divide by 1000 to get seconds
        var todayTime = Date.now() / 1000;
        var bdTime = new Date(birthDate).getTime() / 1000;
        let secondsInOneYear = 60 * 60 * 24 * 365;
        return (todayTime - bdTime) / secondsInOneYear;
    },

    burnGatedView,
    getPercentInRange,
    getColorForPercent,

    /**
     * @param {string} column - The column to sort by
     * @param {Object} sort - The sort object to manage the sort state
     * @param {string} sort.column - The column being sorted
     * @param {string} sort.direction - The current sort direction
     * @returns {void}
     */
    handleSort: (column, sort) => {
        if (column === sort.column) {
            // toggle the order if we're sorting by the same column
            sort.direction = sort.direction === 'asc' ? 'desc' : 'asc';
        } else {
            // reset the order to asc if we're sorting by a new column
            sort.column = column;
            sort.direction = 'asc';
        }
    },

    /**
     * @param {string} column The column to apply the sort class to
     * @returns {string} The sort class to apply
     */
    applySortClass: (column, sort) => {
        if (column !== sort.column) return 'cursor-pointer';
        return sort.direction + ' cursor-pointer';
    },

    getNestedProperty,
    debounce,
    proxyLog,
};

export default DsHelper;
